/**
 * Limpia un número de documento / identificación quitando **solo** espacios, saltos de
 * línea, tabs y caracteres de control.
 *
 * Es el espejo exacto de `GeneralTransformHelper.SanitizarDocumento` del backend, y debe
 * seguir siéndolo: si las dos capas limpian distinto, el front manda un valor y el back
 * guarda otro.
 *
 * Motivo: al pegar desde Excel, Word o un chat, el valor llega con basura invisible
 * (`"1234567 "`, `"1234567\n"`, espacios duros ` `) y termina persistida en base de
 * datos, rompiendo el match candidato↔persona en consultas posteriores.
 *
 * **No quita dígitos ni otros caracteres.** Es deliberado. En base hay documentos
 * corruptos por importación de Excel con notación científica (`"1.01606E+11"`) y hasta
 * texto en la columna equivocada (`"NeivaHuila"`). Reducirlos a dígitos produciría un
 * número plausible pero falso (`"1.01606E+11"` → `"10160611"`), que es peor que dejar el
 * valor malo a la vista: corrompe en silencio. Esos casos se corrigen a mano o por el
 * flujo de corrección de identificación, no borrando caracteres.
 *
 * Para restringir lo que el usuario puede *teclear* en un input, usá {@link soloDigitos}.
 *
 * @param valor Texto crudo tomado del formulario.
 * @returns El valor sin espacios ni caracteres de control. Cadena vacía si entra `null`/`undefined`.
 *
 * @example
 * sanitizarDocumento('1234567 ');      // '1234567'
 * sanitizarDocumento(' 12 345\n');     // '12345'
 * sanitizarDocumento('1.01606E+11');   // '1.01606E+11'  (se preserva, no se mutila)
 * sanitizarDocumento(null);            // ''
 */
export function sanitizarDocumento(valor: string | number | null | undefined): string {
    if (valor === null || valor === undefined) {
        return '';
    }
    // \s cubre espacio, tab, saltos de línea y espacios Unicode (incluido el duro  ).
    // \p{C} cubre los caracteres de control e invisibles que \s no alcanza.
    return valor.toString().replace(/[\s\p{C}]/gu, '');
}

/**
 * Deja únicamente los dígitos de un valor.
 *
 * Pensada para restringir la escritura en un input de cédula (ver `SoloNumerosDirective`),
 * donde descartar lo que el usuario teclea de más es el comportamiento esperado y visible.
 *
 * No la uses para normalizar valores que vienen del backend: ahí destruiría documentos
 * corruptos en vez de conservarlos. Para eso está {@link sanitizarDocumento}.
 *
 * @param valor Texto crudo del input.
 * @returns Solo los dígitos. Cadena vacía si entra `null`/`undefined`.
 *
 * @example
 * soloDigitos('12 34a5');   // '12345'
 * soloDigitos(null);        // ''
 */
export function soloDigitos(valor: string | number | null | undefined): string {
    if (valor === null || valor === undefined) {
        return '';
    }
    return valor.toString().replace(/[^0-9]/g, '');
}
